import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {
  Box,
  Flex,
  Card,
  Button,
  ButtonGroup,
  Heading,
  Text,
} from '@podiumhq/design-system';
import { LogoWrapper, CtaWrapper } from 'components/Trials/styles';

const mobileBreakPoint = 540;
const largerMobileBreakPoint = 840;

const Layout = ({
  children,
  primaryCta,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
  secondaryCta,
  title,
  staticContent,
  subTitle,
  progress,
}) => {
  if (!children || children.length !== 2) return null;

  const renderChildren = () => {
    const logoBoxProps = {
      primaryCta,
      onPrimaryCtaClick,
      onSecondaryCtaClick,
      secondaryCta,
      title,
      staticContent,
      subTitle,
      progress,
    };

    return (
      <Flex height="100%" width="100%">
        <LogoBox {...logoBoxProps}>{children[0]}</LogoBox>

        <StyledVisualBox height="100%" overflow="hidden" background="#EEECEA">
          {children[1]}
        </StyledVisualBox>
      </Flex>
    );
  };
  return (
    <Flex alignItems="flex-end" justifyContent="center">
      <StyledMainCard zIndex="2" position="fixed" bottom="0px">
        {renderChildren()}
      </StyledMainCard>
    </Flex>
  );
};

const CtaButton = ({ cta, onClick, variant, id }) => {
  if (!cta || !onClick) return null;
  return (
    <Button variant={variant} onClick={onClick} data-e2e={id}>
      {cta}
    </Button>
  );
};

const LogoBox = ({
  children,
  secondaryCta,
  onSecondaryCtaClick,
  primaryCta,
  onPrimaryCtaClick,
  title,
  staticContent,
  subTitle,
  progress,
}) => {
  return (
    <LeftCardSection
      p="24px"
      boxSizing="border-box"
      height="100%"
      maxHeight="100%"
      backgroundColor="silver.lightest"
      flexDirection="column"
      flexWrap="nowrap"
    >
      <LogoWrapper flexShrink="0">
        <svg
          width="23"
          height="28"
          viewBox="0 0 23 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0.46582V16.968L7.84824 28.4658H16.9394L12.1782 16.968H22.908V0.46582H0Z"
            fill="#12151A"
          />
        </svg>
        {title && <StyledHeading>{title}</StyledHeading>}
        {subTitle && (
          <Text color="black.lighter" marginBottom="16px">
            {subTitle}
          </Text>
        )}
        {staticContent && staticContent}
      </LogoWrapper>
      <Box overflow="auto" flexGrow="1">
        {children}
      </Box>
      <CtaWrapper>
        <Flex
          paddingTop="24px"
          flexShrink="0"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          <ProgressBar value={progress} />
          <StyledButtonGroup gap={4}>
            <CtaButton
              id="secondary-cta-btn"
              onClick={onSecondaryCtaClick}
              cta={secondaryCta}
            />
            <CtaButton
              id="primary-cta-btn"
              variant="primary"
              cta={primaryCta}
              onClick={onPrimaryCtaClick}
            />
          </StyledButtonGroup>
        </Flex>
      </CtaWrapper>
    </LeftCardSection>
  );
};

const ProgressBar = ({ value }) => {
  return (
    <StyledProgressWrapper
      background="silver.base"
      borderRadius="8px"
      height="10px"
      width="100%"
      marginRight="12px"
    >
      <Box
        position="relative"
        width={`${value}%`}
        background="blue.base"
        height="10px"
        borderRadius="8px"
      />
    </StyledProgressWrapper>
  );
};

const StyledHeading = styled(Heading)`
  margin-bottom: 16px;
  margin-top: 32px;
  @media (max-width: ${mobileBreakPoint}px) {
    margin-bottom: 12px;
    margin-top: 24px;
    font-size: 18px;
  }
`;

const StyledVisualBox = styled(Box)`
  width: 50%;
  @media (max-width: ${largerMobileBreakPoint}px) {
    width: 30%;
  }
  @media (max-width: ${mobileBreakPoint}px) {
    display: none;
  }
`;

const LeftCardSection = styled(Flex)`
  width: 50%;
  @media (max-width: ${largerMobileBreakPoint}px) {
    width: 70%;
  }
  @media (max-width: ${mobileBreakPoint}px) {
    width: 100%;
    padding: 0;
  }
`;

const StyledProgressWrapper = styled(Box)`
  max-width: 147px;
  @media (max-width: ${mobileBreakPoint}px) {
    display: none;
  }
`;

const StyledButtonGroup = styled(ButtonGroup)`
  @media (max-width: ${mobileBreakPoint}px) {
    justify-content: flex-end;
    width: 100%;
  }
`;

const StyledMainCard = styled(Card)`
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  height: 93%;
  width: 93%;
  @media (max-width: ${mobileBreakPoint}px) {
    border-radius: 0;
    height: 100%;
    width: 100%;
  }
`;

const { func, node, string, number } = PropTypes;

Layout.propTypes = {
  children: node.isRequired,
  staticContent: node,
  title: string,
  subTitle: string,
  primaryCta: string,
  onPrimaryCtaClick: func,
  secondaryCta: string,
  onSecondaryCtaClick: func,
  progress: number,
};

Layout.defaultProps = {
  staticContent: null,
  title: '',
  subTitle: '',
  primaryCta: '',
  onPrimaryCtaClick: () => {},
  secondaryCta: '',
  onSecondaryCtaClick: () => {},
  progress: 0,
};

export default Layout;
